import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaExclamationTriangle as TriangleAlert, FaInfoCircle as Info } from 'react-icons/fa';
import { LuSparkles as Sparkles } from 'react-icons/lu';

import {
  ActionButton,
  Field,
  Notice,
  PanelHeader,
  RecommendResult,
  Section,
  Segment,
  SimilarCasesTable,
  SizeCompareTable
} from './formPrimitives';
import { fetchRevisionHistory, fetchSavedReport } from './listApi';
import {
  applyBirthAndExamAge,
  buildChangeSummary,
  buildPatientPayload,
  createInitialForm,
  formatDate,
  getSessionUser,
  historyRowToForm,
  inputsEqual,
  isBlank,
  isRecommendAction,
  pickLatestUsedLens
} from './v2Helpers';
import {
  compareSizes,
  fetchSimilarCases,
  numeric,
  recommendLens,
  saveLensRecommendResult,
  sphericalEquivalent,
  suggestedOrderSphere
} from '../../utils/lensRecommend';

const SIMILAR_CASE_TOP_K = 5;

function examPreview(values, ageYears) {
  const age = Number.isFinite(Number(ageYears)) ? Number(ageYears) : null;
  const sph = numeric(values.opmr_sphere, -8);
  const cyl = numeric(values.opmr_cylinder, -1);
  const opmrSe = sphericalEquivalent(sph, cyl);
  const suggested = suggestedOrderSphere(age ?? 30, sph, cyl);
  const acd = numeric(values.acd_preop, 0);
  const axisLabel = values.orientation === '수직' ? 'V' : 'H';
  return { opmrSe, suggested, acd, axisLabel };
}

const REQUIRED_FIELDS = [
  'chart_no',
  'birth_date',
  'exam_date',
  'opmr_sphere',
  'opmr_cylinder',
  'opmr_axis',
  'k1',
  'k2',
  'k_axis',
  'acd_preop',
  'wtw',
  'ct',
  'casia_acw',
  'casia_clr',
  'casia_ata',
  'casia_acd'
];

function mergeWarnings(...lists) {
  return Array.from(new Set(lists.flat().filter(Boolean)));
}

/**
 * 최초(recommend) 탭
 * recordMode: new(아직 저장 이력 없음) | existing
 * 신규/수정 모두 이 화면에서 「최적 추천 실행」으로 처리한다.
 * - 저장 이력이 없으면 create INSERT
 * - 있으면서 입력이 바뀌었으면 update(후속 INSERT)
 */
function EyeExamBlock({
  values,
  onChange,
  ageYears,
  errors = {},
  orientation
}) {
  const resolvedOrientation = orientation || values.orientation;
  const preview = examPreview({ ...values, orientation: resolvedOrientation }, ageYears);
  return (
    <>
      <Section title="OPMR / 각막">
        <div className="lens-grid">
          <Field label="OPMR Sphere" value={values.opmr_sphere} onChangeText={(v) => onChange('opmr_sphere', v)} numeric error={errors.opmr_sphere} />
          <Field label="OPMR Cylinder" value={values.opmr_cylinder} onChangeText={(v) => onChange('opmr_cylinder', v)} numeric error={errors.opmr_cylinder} />
          <Field label="OPMR Axis" value={values.opmr_axis} onChangeText={(v) => onChange('opmr_axis', v)} numeric error={errors.opmr_axis} />
          <Field label="K1" value={values.k1} onChangeText={(v) => onChange('k1', v)} numeric error={errors.k1} />
          <Field label="K2" value={values.k2} onChangeText={(v) => onChange('k2', v)} numeric error={errors.k2} />
          <Field label="K Axis" value={values.k_axis} onChangeText={(v) => onChange('k_axis', v)} numeric error={errors.k_axis} />
        </div>
        <Notice tone="info" icon={<Info size={15} color="#1d4ed8" />}>
          OPMR SE {preview.opmrSe.toFixed(2)}D · 권장 Sphere {preview.suggested.sphere.toFixed(2)}D · {preview.suggested.note}
        </Notice>
      </Section>
      <div className="lens-compact-pair">
        <Section title="생체계측">
          <div className="lens-grid">
            <Field label="ACD" value={values.acd_preop} onChangeText={(v) => onChange('acd_preop', v)} numeric error={errors.acd_preop} />
            <Field label="WTW" value={values.wtw} onChangeText={(v) => onChange('wtw', v)} numeric error={errors.wtw} />
            <Field label="CT" value={values.ct} onChangeText={(v) => onChange('ct', v)} numeric error={errors.ct} />
            <Field label="Expected SE" value={values.exp_se} onChangeText={(v) => onChange('exp_se', v)} numeric error={errors.exp_se} />
          </div>
          {preview.acd < 2.8 ? (
            <Notice tone="warn" icon={<TriangleAlert size={15} color="#b45309" />}>
              ACD {preview.acd.toFixed(2)}mm: 2.8mm 미만, CASIA2 ACD 대신 생체계측 ACD가 반영됩니다.
            </Notice>
          ) : (
            <Notice tone="info" icon={<Info size={15} color="#1d4ed8" />}>
              예측 기준 ACD는 CASIA2 {preview.axisLabel} ACD입니다.
            </Notice>
          )}
        </Section>
        <Section title={`CASIA2 (${resolvedOrientation})`}>
          <div className="lens-grid">
            <Field label={`${preview.axisLabel} ACW`} value={values.casia_acw} onChangeText={(v) => onChange('casia_acw', v)} numeric error={errors.casia_acw} />
            <Field label={`${preview.axisLabel} CLR`} value={values.casia_clr} onChangeText={(v) => onChange('casia_clr', v)} numeric error={errors.casia_clr} />
            <Field label={`${preview.axisLabel} ATA`} value={values.casia_ata} onChangeText={(v) => onChange('casia_ata', v)} numeric error={errors.casia_ata} />
            <Field label={`${preview.axisLabel} ACD`} value={values.casia_acd} onChangeText={(v) => onChange('casia_acd', v)} numeric error={errors.casia_acd} />
          </div>
        </Section>
      </div>
    </>
  );
}

export default function LensRecommendTab({
  recordMode = 'existing',
  chartNo = '',
  eye = 'OD',
  caseId: initialCaseId = '',
  seedRow = null,
  onSaved
}) {
  const [form, setForm] = useState(() => {
    if (seedRow) return historyRowToForm(seedRow);
    const initial = createInitialForm();
    return {
      ...initial,
      case_id: initialCaseId || '',
      chart_no: chartNo || '',
      eye: recordMode === 'new' ? 'OU' : (eye || 'OD')
    };
  });
  const [eyeMode, setEyeMode] = useState(
    recordMode === 'new' || eye === 'OU' ? '양안' : (eye || 'OD')
  );
  // 저장 전 신규 상태에서만 차트번호/Eye 를 수정할 수 있다.
  const [isNewRecord, setIsNewRecord] = useState(recordMode === 'new');
  const [caseId, setCaseId] = useState(
    initialCaseId
    || (seedRow && (seedRow.caseId || seedRow.case_id))
    || ''
  );
  const [baselineSnapshot, setBaselineSnapshot] = useState(null);
  const [result, setResult] = useState(null);
  const [sizeCompare, setSizeCompare] = useState([]);
  const [similarCases, setSimilarCases] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [revisions, setRevisions] = useState([]);
  const [historyTick, setHistoryTick] = useState(0);
  const [reportLoading, setReportLoading] = useState(false);
  const [savedReportDt, setSavedReportDt] = useState('');
  // 화면에 표시 중인 입력값이 서버 최신본보다 우선하는지(직접 저장한 직후) 여부
  const formOwnedRef = useRef(false);

  const computed = useMemo(() => {
    const age = Number.isFinite(Number(form.age_years)) ? Number(form.age_years) : null;
    const sph = numeric(form.opmr_sphere, -8);
    const cyl = numeric(form.opmr_cylinder, -1);
    const opmrSe = sphericalEquivalent(sph, cyl);
    const suggested = suggestedOrderSphere(age ?? 30, sph, cyl);
    const acd = numeric(form.acd_preop, 0);
    return { age, opmrSe, suggested, acd };
  }, [form]);

  useEffect(() => {
    if (isNewRecord || !form.chart_no) return undefined;

    let cancelled = false;
    const loadHistory = async () => {
      const user = getSessionUser();
      if (!user?.hospitalId) return;

      const endDt = formatDate();
      const start = new Date();
      start.setFullYear(start.getFullYear() - 2);

      try {
        const rows = await fetchRevisionHistory({
          chartNo: form.chart_no,
          hospitalId: user.hospitalId,
          startDt: formatDate(start),
          endDt,
          caseId: caseId || undefined
        });
        if (cancelled) return;

        const eyeRows = rows.filter((row) => (row.eye || 'OD') === form.eye);
        const caseRows = caseId
          ? eyeRows.filter((row) => String(row.caseId || row.case_id || '') === String(caseId))
          : eyeRows;
        const filtered = caseRows.filter(isRecommendAction);
        setRevisions(filtered);

        // 이 화면에서 직접 저장한 직후라면 서버 값으로 입력 폼/리포트를 덮어쓰지 않는다.
        if (formOwnedRef.current) return;

        const usedLens = pickLatestUsedLens(caseRows);

        if (filtered[0] && !seedRow) {
          const latestForm = {
            ...historyRowToForm(filtered[0]),
            ...usedLens
          };
          setForm(latestForm);
          setBaselineSnapshot(latestForm);
          if (latestForm.case_id) setCaseId(latestForm.case_id);
        } else if (seedRow) {
          const seedForm = {
            ...historyRowToForm(seedRow),
            ...usedLens
          };
          setForm(seedForm);
          setBaselineSnapshot(seedForm);
          if (seedForm.case_id) setCaseId(seedForm.case_id);
        }

        // 저장 시점의 추천 리포트를 그대로 복원한다.
        const latestExamId = filtered[0]?.examId;
        if (!latestExamId) return;

        setReportLoading(true);
        try {
          const saved = await fetchSavedReport({
            examId: latestExamId,
            hospitalId: user.hospitalId
          });
          if (cancelled || !saved) return;

          setResult(saved.result);
          setSizeCompare(saved.sizeCompare);
          setSimilarCases(saved.similarCases);
          setWarnings(saved.warnings);
          setSavedReportDt(saved.examDt || '');
        } catch (reportError) {
          if (!cancelled) {
            console.error('[LensRecommendTab] saved report load failed', reportError);
          }
        } finally {
          if (!cancelled) setReportLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[LensRecommendTab] revision load failed', err);
        }
      }
    };

    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [isNewRecord, form.chart_no, form.eye, caseId, seedRow, historyTick]);

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setValidationErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
    setSaveMessage('');
  };

  const updateBirthDate = (value) => {
    if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
    setForm((current) => applyBirthAndExamAge(current, value, current.exam_date));
    setValidationErrors((current) => {
      if (!current.birth_date) return current;
      const next = { ...current };
      delete next.birth_date;
      return next;
    });
  };

  const updateExamDate = (value) => {
    setForm((current) => applyBirthAndExamAge(current, current.birth_date, value));
  };

  const bothEyes = form.eye === 'OU' || (isNewRecord && eyeMode === '양안');

  const selectEyeMode = (next) => {
    if (!isNewRecord || bothEyes) return;
    update('eye', next);
    setEyeMode(next);
  };

  const selectEyeScope = (next) => {
    if (!isNewRecord) return;
    if (next === '양안') {
      update('eye', 'OU');
      setEyeMode('양안');
      return;
    }
    const singleEye = form.eye === 'OS' ? 'OS' : 'OD';
    update('eye', singleEye);
    setEyeMode(singleEye);
  };

  const validateForm = () => {
    const errors = {};
    REQUIRED_FIELDS.forEach((fieldName) => {
      if (isBlank(form[fieldName])) {
        errors[fieldName] = '필수 입력값입니다.';
        return;
      }
      if (fieldName === 'birth_date' || fieldName === 'exam_date') return;
      if (!Number.isFinite(Number(form[fieldName])) && fieldName !== 'chart_no') {
        // chart_no can be non-numeric string
      }
      if (
        ![
          'chart_no',
          'birth_date',
          'exam_date'
        ].includes(fieldName) && !Number.isFinite(Number(form[fieldName]))
      ) {
        errors[fieldName] = '숫자만 입력해주세요.';
      }
    });
    return errors;
  };

  const runRecommend = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length) {
      setValidationErrors(errors);
      setResult(null);
      setError('');
      return;
    }

    if (!isNewRecord && baselineSnapshot && inputsEqual(baselineSnapshot, form)) {
      setSaveMessage('입력값이 이전과 동일하여 저장하지 않았습니다.');
      setError('');
      return;
    }

    setLoading(true);
    setError('');
    setSaveMessage('');
    setValidationErrors({});

    try {
      const patientPayload = buildPatientPayload(form, computed.age);
      const response = await recommendLens(patientPayload);
      const recommendedLens = {
        size_mm: response.result.recommended.size_mm,
        sphere: response.result.recommended.sphere,
        cylinder: numeric(form.opmr_cylinder, -1)
      };

      const [compareResponse, similarResponse] = await Promise.all([
        compareSizes(patientPayload, recommendedLens),
        fetchSimilarCases(patientPayload, SIMILAR_CASE_TOP_K)
      ]);

      const mergedWarnings = mergeWarnings(
        response.warnings,
        compareResponse.warnings,
        similarResponse.warnings
      );

      setResult(response.result.recommended);
      setSizeCompare(compareResponse.result);
      setSimilarCases(similarResponse.result);
      setWarnings(mergedWarnings);
      setSavedReportDt('');

      // 실제 사용 렌즈는 사용자 입력값만 유지한다. (추천값으로 채우지 않음)
      const savedForm = {
        ...form,
        lens_size: response.result.recommended.size_mm,
        lens_sphere: String(response.result.recommended.sphere)
      };
      setForm(savedForm);

      const user = getSessionUser();
      if (!user?.userNo) {
        throw new Error('로그인 사용자 정보가 없어 저장하지 못했습니다.');
      }

      const revisionKind = isNewRecord || !baselineSnapshot ? 'create' : 'update';

      const saveResponse = await saveLensRecommendResult({
        userNo: user.userNo,
        hospitalId: user.hospitalId,
        actionType: 'recommend',
        revisionKind,
        caseId: caseId || undefined,
        form: {
          ...savedForm,
          case_id: caseId || undefined,
          revision_kind: revisionKind,
          age_group: form.age_group
        },
        patientPayload,
        lensPayload: recommendedLens,
        result: response.result.recommended,
        sizeCompare: compareResponse.result,
        similarCases: similarResponse.result,
        warnings: mergedWarnings
      });

      const savedCaseId = saveResponse?.caseId || caseId || '';
      if (savedCaseId) {
        setCaseId(savedCaseId);
        setForm((current) => ({ ...current, case_id: savedCaseId }));
      }

      formOwnedRef.current = true;
      setBaselineSnapshot({ ...savedForm, case_id: savedCaseId });
      setIsNewRecord(false);
      setHistoryTick((tick) => tick + 1);
      setSaveMessage(
        revisionKind === 'create'
          ? '렌즈 추천 결과가 저장되었습니다. 아래 입력 히스토리에서 확인할 수 있습니다.'
          : '수정분이 새 이력으로 저장되었습니다.'
      );

      onSaved?.({
        chartNo: form.chart_no,
        eye: form.eye,
        caseId: savedCaseId,
        revisionKind,
        result: response.result.recommended
      });
    } catch (err) {
      setResult(null);
      setSizeCompare([]);
      setSimilarCases([]);
      setError(err instanceof Error ? err.message : '추천 요청 실패');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 추천 API를 다시 호출하지 않고, 사용자가 입력한 실제 사용 렌즈만 후속 INSERT 한다.
   */
  const saveUsedLensOnly = async () => {
    if (isNewRecord || !baselineSnapshot) {
      setError('먼저 「최적 추천 실행」으로 저장해 주세요.');
      return;
    }
    if (!result) {
      setError('저장된 추천 결과가 없어 실제 렌즈만 저장할 수 없습니다. 상세를 다시 열어주세요.');
      return;
    }
    if (
      isBlank(form.used_lens_size)
      && isBlank(form.used_lens_sphere)
      && isBlank(form.surgery_date)
    ) {
      setError('수술일 또는 실제 사용 렌즈 Size/Sphere를 입력해주세요.');
      return;
    }
    if (baselineSnapshot && inputsEqual(baselineSnapshot, form)) {
      setSaveMessage('실제 사용 렌즈 입력값이 이전과 동일하여 저장하지 않았습니다.');
      return;
    }

    setLoading(true);
    setError('');
    setSaveMessage('');

    try {
      const user = getSessionUser();
      if (!user?.userNo) {
        throw new Error('로그인 사용자 정보가 없어 저장하지 못했습니다.');
      }

      const patientPayload = buildPatientPayload(form, computed.age);
      const revisionKind = 'update';
      const lensPayload = {
        size_mm: result.size_mm,
        sphere: result.sphere,
        cylinder: numeric(form.opmr_cylinder, -1)
      };

      await saveLensRecommendResult({
        userNo: user.userNo,
        hospitalId: user.hospitalId,
        actionType: 'recommend',
        revisionKind,
        caseId: caseId || undefined,
        form: {
          ...form,
          case_id: caseId || undefined,
          revision_kind: revisionKind,
          age_group: form.age_group
        },
        patientPayload,
        lensPayload,
        result,
        sizeCompare,
        similarCases,
        warnings
      });

      formOwnedRef.current = true;
      setBaselineSnapshot({ ...form, case_id: caseId || form.case_id || '' });
      setHistoryTick((tick) => tick + 1);
      setSaveMessage('실제 사용 렌즈가 수정분으로 저장되었습니다.');
    } catch (err) {
      setError(err instanceof Error ? err.message : '실제 렌즈 저장 실패');
    } finally {
      setLoading(false);
    }
  };

  const busy = loading || reportLoading;
  const showReport = busy || Boolean(error) || Boolean(result) || !isNewRecord;

  // 수정이력 목록
  const revisionView = revisions.map((row, index, all) => {
    const newer = all[index];
    const older = all[index + 1];
    return {
      key: row.examId || `${row.examDt}-${index}`,
      dt: row.examDt || '',
      summary: buildChangeSummary(
        older ? historyRowToForm(older) : null,
        historyRowToForm(newer)
      )
    };
  });

  return (
    <div className="lens-v2-tab-root">
      <main className="lens-workspace">
        <div className="lens-input-column lens-panel">
          <PanelHeader title={isNewRecord ? '새 렌즈 추천' : '렌즈 추천 입력'} label="" />
          <div className="lens-panel-body">
            <Section title="환자 기본 정보">
              <div className="lens-grid">
                <Field
                  label="검사일"
                  value={form.exam_date}
                  onChangeText={updateExamDate}
                  type="date"
                  error={validationErrors.exam_date}
                  hint="나이 계산 기준일"
                />
                <Field
                  label="차트번호"
                  value={form.chart_no}
                  onChangeText={(value) => update('chart_no', value)}
                  readOnly={!isNewRecord}
                  error={validationErrors.chart_no}
                />
                <Segment
                  label="성별"
                  value={form.gender}
                  options={['F', 'M']}
                  onChange={(value) => update('gender', value)}
                />
                <Field
                  label="생년월일"
                  value={form.birth_date}
                  onChangeText={updateBirthDate}
                  type="date"
                  min="1900-01-01"
                  max={formatDate()}
                  error={validationErrors.birth_date}
                />
                <Field
                  className="lens-age-field"
                  label="나이"
                  value={form.age_years}
                  readOnly
                />
                <Field label="연령대" value={form.age_group} readOnly />
                <div className={`lens-field lens-eye-choice${isNewRecord ? '' : ' lens-eye-choice-single'}`}>
                  <span className="lens-label">Eye</span>
                  <div className="lens-eye-choice-row">
                    {isNewRecord ? (
                      <Segment
                        bare
                        value={bothEyes ? '양안' : '단안'}
                        options={['양안', '단안']}
                        onChange={selectEyeScope}
                      />
                    ) : null}
                    <div className="lens-eye-side">
                      {bothEyes ? (
                        <span className="lens-eye-both-label">OD · OS</span>
                      ) : isNewRecord ? (
                        <Segment
                          bare
                          value={form.eye}
                          options={['OD', 'OS']}
                          onChange={selectEyeMode}
                        />
                      ) : (
                        <span className="lens-eye-both-label">{form.eye}</span>
                      )}
                    </div>
                  </div>
                </div>
                <Segment
                  label="삽입 방향"
                  value={form.orientation}
                  options={['수평', '수직']}
                  onChange={(value) => update('orientation', value)}
                />
              </div>
            </Section>

            <EyeExamBlock
              values={form}
              onChange={update}
              errors={validationErrors}
              ageYears={form.age_years}
              orientation={form.orientation}
            />

            <Section title="추천 실행">
              <div className="lens-recommend-action-panel">
                <div className="lens-recommend-action-copy">
                  <strong>검사값으로 최적 렌즈 추천</strong>
                </div>
                <div className="lens-actions">
                  <ActionButton
                    label="최적 추천 실행"
                    icon={<Sparkles size={16} color="#ffffff" />}
                    onClick={runRecommend}
                    primary
                    disabled={loading}
                  />
                </div>
              </div>
              {saveMessage && !/실제 사용 렌즈/.test(saveMessage) ? (
                <Notice tone="info" icon={<Info size={15} color="#1d4ed8" />}>{saveMessage}</Notice>
              ) : null}
            </Section>
          </div>
        </div>

        {showReport ? (
          <div className="lens-result-column lens-result-panel">
            <PanelHeader title="추천 리포트" label="" dark />
            <div className="lens-panel-body lens-result-panel-body">
              <section className="lens-result-section">
                <h2 className="lens-result-section-title">
                  추천 결과
                  {savedReportDt ? (
                    <span className="lens-v2-pill" style={{ marginLeft: 8 }}>
                      저장본 {savedReportDt}
                    </span>
                  ) : null}
                </h2>
                {busy ? <div className="lens-loading" role="status" aria-label="로딩 중" /> : null}
                {error ? (
                  <Notice tone="bad" icon={<TriangleAlert size={15} color="#b91c1c" />}>{error}</Notice>
                ) : null}
                {result ? (
                  <div className="lens-report-stack">
                    <RecommendResult result={result} />
                    {sizeCompare.length ? <SizeCompareTable rows={sizeCompare} /> : null}
                    {warnings.length ? (
                      <div className="lens-report-warnings">
                        {warnings.map((warning) => (
                          <Notice key={warning} tone="warn" icon={<TriangleAlert size={15} color="#b45309" />}>
                            {warning}
                          </Notice>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <p className="lens-empty-text">검사값을 입력한 뒤 추천을 실행하세요.</p>
                )}
              </section>
            </div>
          </div>
        ) : null}
      </main>

      <section className="lens-v2-used-lens-card">
        <div className="lens-v2-used-lens-head">
          <h3>실제 사용 렌즈</h3>
          {result ? (
            <span className="lens-v2-used-lens-ref">
              추천 {result.size_mm}mm · Sphere {result.sphere}D
            </span>
          ) : null}
        </div>
        <p className="lens-v2-used-lens-desc">
          수술일과 실제 삽입 렌즈입니다. 추천 결과와 달라도 되며, 입력한 값만 저장됩니다.
        </p>
        <div className="lens-v2-used-lens-row">
          <Field
            label="수술일"
            type="date"
            value={form.surgery_date}
            onChangeText={(v) => update('surgery_date', v)}
            readOnly={isNewRecord}
          />
          <Field
            label="Size (mm)"
            value={form.used_lens_size}
            onChangeText={(v) => update('used_lens_size', v)}
            numeric
            readOnly={isNewRecord}
          />
          <Field
            label="Sphere (D)"
            value={form.used_lens_sphere}
            onChangeText={(v) => update('used_lens_sphere', v)}
            numeric
            readOnly={isNewRecord}
          />
          <Field
            label="Cylinder (D)"
            value={form.used_lens_cylinder}
            onChangeText={(v) => update('used_lens_cylinder', v)}
            numeric
            readOnly={isNewRecord}
          />
          <div className="lens-v2-used-lens-save">
            <button
              className="lens-v2-secondary-btn"
              type="button"
              onClick={saveUsedLensOnly}
              disabled={loading || isNewRecord}
              title={isNewRecord ? '먼저 「최적 추천 실행」으로 저장해 주세요.' : undefined}
            >
              저장
            </button>
          </div>
        </div>
        {isNewRecord ? (
          <p className="lens-v2-used-lens-desc">
            「최적 추천 실행」으로 저장한 뒤 입력할 수 있습니다.
          </p>
        ) : null}
        {saveMessage && /실제 사용 렌즈/.test(saveMessage) ? (
          <Notice tone="info" icon={<Info size={15} color="#1d4ed8" />}>{saveMessage}</Notice>
        ) : null}
      </section>

      {similarCases.length ? (
        <section className="lens-similar-result">
          <SimilarCasesTable rows={similarCases} />
        </section>
      ) : null}

      <section className="lens-v2-history-card" style={{ marginTop: 12 }}>
        <div className="lens-v2-history-title">
          <h3>수정이력</h3>
          <span className="lens-v2-pill">{revisionView.length}건</span>
        </div>
        {revisionView.length ? (
          <div className="lens-v2-table-wrap">
            <table className="lens-v2-table">
              <thead>
                <tr>
                  <th>수정일시</th>
                  <th>변경내용</th>
                </tr>
              </thead>
              <tbody>
                {revisionView.map((row) => (
                  <tr key={row.key}>
                    <td>{row.dt || '—'}</td>
                    <td>
                      <p className="lens-v2-change-summary">{row.summary}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="lens-v2-empty">
            {isNewRecord
              ? '아직 저장 전입니다. 「최적 추천 실행」 시 이력이 생성됩니다.'
              : '저장된 이력이 없습니다.'}
          </div>
        )}
      </section>
    </div>
  );
}
